import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { type SanityCombinedListingProductFragment } from '@data/sanity/queries/types/product'
import { type DropdownOption } from '@lib/helpers'
import { ProductCombinedListingContext } from '@lib/product/combined-listing/context'
import { getCombinedListingProductDefaultValues } from '@lib/product/combined-listing/defaults'
import {
  getCombinedListingProductDropdownOptions,
  getIsEmptyFrame,
} from '@lib/product/combined-listing/helpers'
import { getCombinedListingProductsBySize } from '@lib/product/combined-listing/product'
import { type CombinedListingProductType } from '@lib/product/combined-listing/types'
import { StringsContext } from '@lib/strings-context'

import Icon from '@components/icon'
import InputDropdown from '@components/input-dropdown/with-managed-state'
import InputField from '@components/input-field/with-managed-state'
import NewLineToBr from '@components/new-line-to-br'
import Tooltip from '@components/tooltip'
import SmallGraphic from './small-graphic'

interface CombinedListingProductSelectProps {
  onMainProductChange: (product: SanityCombinedListingProductFragment) => void
  frameColor?: string
}

const CombinedListingProductSelect = ({
  onMainProductChange,
  frameColor,
}: CombinedListingProductSelectProps) => {
  const strings = useContext(StringsContext)
  const {
    combinedListingSettings,
    combinedListingConfiguration,
    combinedListingMainProductId,
    combinedListingProducts,
    combinedListingFrameSize,
    combinedListingArtworkSize,
    addCombinedListingProduct,
    clearCombinedListingProductType,
    setCombinedListingFrameSize,
    setCombinedListingArtworkSize,
  } = useContext(ProductCombinedListingContext)

  const [artworkSize, setArtworkSize] = useState('')

  const isDefaultSelectionLoadedRef = useRef(false)
  const isArtworkSizeLoadedRef = useRef(false)

  // Product IDs for dropdown input values
  const glassId = useMemo(() => {
    return combinedListingProducts?.find(
      (product) => product.combinedListingProductType === 'glass',
    )?.productID
  }, [combinedListingProducts])
  const backboardId = useMemo(() => {
    return combinedListingProducts?.find(
      (product) => product.combinedListingProductType === 'backboard',
    )?.productID
  }, [combinedListingProducts])
  const passepartoutId = useMemo(() => {
    return combinedListingProducts?.find(
      (product) => product.combinedListingProductType === 'passepartout',
    )?.productID
  }, [combinedListingProducts])
  const spacerId = useMemo(() => {
    return combinedListingProducts?.find(
      (product) => product.combinedListingProductType === 'spacer',
    )?.productID
  }, [combinedListingProducts])

  // Options for dropdown inputs
  const frameOptions = useMemo(() => {
    return getCombinedListingProductDropdownOptions(
      combinedListingConfiguration?.frame ?? [],
      null,
    )
  }, [combinedListingConfiguration])
  const sizeOptions = useMemo<DropdownOption[]>(() => {
    const combinedListing = getCombinedListingProductsBySize(
      combinedListingConfiguration?.frame ?? [],
      combinedListingConfiguration?.glass ?? [],
      combinedListingConfiguration?.backboard ?? [],
      combinedListingConfiguration?.passepartout ?? [],
      combinedListingConfiguration?.spacer ?? [],
    )

    return Object.keys(combinedListing).map((size) => ({
      value: size,
      title: size,
    }))
  }, [combinedListingConfiguration])
  const glassOptions = useMemo(() => {
    return getCombinedListingProductDropdownOptions(
      combinedListingConfiguration?.glass ?? [],
      combinedListingFrameSize ?? null,
      strings.combinedListingNoGlass,
    )
  }, [combinedListingFrameSize, combinedListingConfiguration, strings])
  const backboardOptions = useMemo(() => {
    return getCombinedListingProductDropdownOptions(
      combinedListingConfiguration?.backboard ?? [],
      combinedListingFrameSize ?? null,
      strings.combinedListingNoBackboard,
    )
  }, [combinedListingFrameSize, combinedListingConfiguration, strings])
  const passepartoutOptions = useMemo(() => {
    return getCombinedListingProductDropdownOptions(
      combinedListingConfiguration?.passepartout ?? [],
      combinedListingFrameSize ?? null,
      strings.combinedListingNoPassepartout,
    )
  }, [combinedListingFrameSize, combinedListingConfiguration, strings])
  const spacerOptions = useMemo(() => {
    return getCombinedListingProductDropdownOptions(
      combinedListingConfiguration?.spacer ?? [],
      combinedListingFrameSize ?? null,
      strings.combinedListingNoSpacer,
    )
  }, [combinedListingFrameSize, combinedListingConfiguration, strings])
  const hasGlassOptions = useMemo(() => {
    return glassOptions.some((option) => !!option.value)
  }, [glassOptions])
  const hasBackboardOptions = useMemo(() => {
    return backboardOptions.some((option) => !!option.value)
  }, [backboardOptions])
  const hasPassepartoutOptions = useMemo(() => {
    return passepartoutOptions.some((option) => !!option.value)
  }, [passepartoutOptions])
  const hasSpacerOptions = useMemo(() => {
    return spacerOptions.some((option) => !!option.value)
  }, [spacerOptions])

  const isEmptyFrame = useMemo(() => {
    return getIsEmptyFrame(combinedListingProducts ?? [])
  }, [combinedListingProducts])

  // Load artwork size into internal state variable
  useEffect(() => {
    if (
      typeof combinedListingArtworkSize === 'undefined' ||
      isArtworkSizeLoadedRef.current
    ) {
      return
    }

    setArtworkSize(combinedListingArtworkSize ?? '')
    isArtworkSizeLoadedRef.current = true
  }, [combinedListingArtworkSize])

  /**
   * Validates value for product type and deselects it, if it's invalid.
   */
  const validateProductType = useCallback(
    (
      type: CombinedListingProductType,
      frameSize: string,
      productId?: number,
    ) => {
      if (!productId) {
        return
      }

      const options = getCombinedListingProductDropdownOptions(
        combinedListingConfiguration?.[type] ?? [],
        frameSize,
      )
      const isValid = options.some((option) => option.value === `${productId}`)

      // Deselect products that don't have a matching size
      if (!isValid) {
        clearCombinedListingProductType(type)
      }
    },
    [clearCombinedListingProductType, combinedListingConfiguration],
  )

  /**
   * Selects default glass, backboard, passepartout and spacer products.
   */
  const selectDefaults = useCallback(
    (newFrameSize: string) => {
      // Select default values only once
      if (
        !combinedListingSettings ||
        !combinedListingConfiguration ||
        isDefaultSelectionLoadedRef.current
      ) {
        return
      }

      isDefaultSelectionLoadedRef.current = true

      // Stop if any product is already selected
      if (glassId || backboardId || passepartoutId || spacerId) {
        return
      }

      const defaultValues = getCombinedListingProductDefaultValues(
        combinedListingSettings,
        combinedListingConfiguration,
        newFrameSize,
      )
      const defaultProductIds = [
        defaultValues.glassProductId,
        defaultValues.backboardProductId,
        defaultValues.passepartoutProductId,
        defaultValues.spacerProductId,
      ].filter(Boolean) as number[]

      defaultProductIds.forEach((productId) => {
        addCombinedListingProduct(productId)
      })
    },
    [
      addCombinedListingProduct,
      backboardId,
      combinedListingConfiguration,
      combinedListingSettings,
      glassId,
      passepartoutId,
      spacerId,
    ],
  )

  const handleFrameChange = useCallback(
    (newProductId: string) => {
      if (!newProductId) {
        return
      }

      const newProduct = combinedListingConfiguration?.frame?.find(
        (product) => product.productID === Number(newProductId),
      )

      if (!newProduct) {
        return
      }

      onMainProductChange(newProduct)
    },
    [combinedListingConfiguration, onMainProductChange],
  )

  const handleSizeChange = useCallback(
    (newSize: string) => {
      if (!newSize) {
        return
      }

      setCombinedListingFrameSize(newSize)

      validateProductType('glass', newSize, glassId)
      validateProductType('backboard', newSize, backboardId)
      validateProductType('passepartout', newSize, passepartoutId)
      validateProductType('spacer', newSize, spacerId)

      selectDefaults(newSize)
    },
    [
      backboardId,
      glassId,
      passepartoutId,
      selectDefaults,
      setCombinedListingFrameSize,
      spacerId,
      validateProductType,
    ],
  )

  const handleGlassChange = useCallback(
    (newProductId: string) => {
      isDefaultSelectionLoadedRef.current = true

      if (!newProductId) {
        clearCombinedListingProductType('glass')
        return
      }

      addCombinedListingProduct(Number(newProductId))
    },
    [addCombinedListingProduct, clearCombinedListingProductType],
  )

  const handleBackboardChange = useCallback(
    (newProductId: string) => {
      isDefaultSelectionLoadedRef.current = true

      if (!newProductId) {
        clearCombinedListingProductType('backboard')
        return
      }

      addCombinedListingProduct(Number(newProductId))
    },
    [addCombinedListingProduct, clearCombinedListingProductType],
  )

  const handlePassepartoutChange = useCallback(
    (newProductId: string) => {
      isDefaultSelectionLoadedRef.current = true

      if (!newProductId) {
        clearCombinedListingProductType('passepartout')
        setCombinedListingArtworkSize(null)
        setArtworkSize('')
        return
      }

      addCombinedListingProduct(Number(newProductId))
    },
    [
      addCombinedListingProduct,
      clearCombinedListingProductType,
      setArtworkSize,
      setCombinedListingArtworkSize,
    ],
  )

  const handleArtworkSizeChange = useCallback(
    (newArtworkSize: string) => {
      isDefaultSelectionLoadedRef.current = true
      setArtworkSize(newArtworkSize)

      if (!newArtworkSize) {
        setCombinedListingArtworkSize(null)
        return
      }

      setCombinedListingArtworkSize(newArtworkSize)
    },
    [setCombinedListingArtworkSize, setArtworkSize],
  )

  const handleSpacerChange = useCallback(
    (newProductId: string) => {
      if (!newProductId) {
        clearCombinedListingProductType('spacer')
        return
      }

      addCombinedListingProduct(Number(newProductId))
    },
    [addCombinedListingProduct, clearCombinedListingProductType],
  )

  return (
    <div className="max-w-sm w-full space-y-5">
      <label className="font-medium mb-2">
        {frameColor
          ? `${strings.combinedListingFrameColor} ${frameColor}`
          : strings.combinedListingFrame}
      </label>
      <div className="flex flex-wrap gap-x-2 gap-y-1.5">
        {frameOptions.map((frameOption) => (
          <SmallGraphic
            key={frameOption.value}
            selected={
              combinedListingMainProductId === Number(frameOption.value)
            }
            backgroundColor={frameOption.color}
            backgroundImage={frameOption.imageUrl}
            onClick={() => handleFrameChange(frameOption.value)}
          />
        ))}
      </div>

      <InputDropdown
        value={combinedListingFrameSize ?? ''}
        setValue={handleSizeChange}
        label={strings.combinedListingSize}
        placeholder={strings.combinedListingSizePlaceholder}
        options={sizeOptions}
      />

      {!!combinedListingFrameSize && (
        <>
          {isEmptyFrame && (
            <div className="inline-block px-4 py-2 rounded-md bg-gray-700 text-pageBG">
              {strings.combinedListingEmptyFrame}
            </div>
          )}

          {hasGlassOptions && (
            <InputDropdown
              value={glassId ? `${glassId}` : ''}
              setValue={handleGlassChange}
              label={strings.combinedListingGlass}
              options={glassOptions}
            />
          )}

          {hasBackboardOptions && (
            <InputDropdown
              value={backboardId ? `${backboardId}` : ''}
              setValue={handleBackboardChange}
              label={strings.combinedListingBackboard}
              options={backboardOptions}
            />
          )}

          {hasPassepartoutOptions && (
            <InputDropdown
              value={passepartoutId ? `${passepartoutId}` : ''}
              setValue={handlePassepartoutChange}
              label={strings.combinedListingPassepartout}
              options={passepartoutOptions}
            />
          )}

          {hasPassepartoutOptions && !!passepartoutId && (
            <InputField
              type="text"
              value={artworkSize}
              setValue={handleArtworkSizeChange}
              label={
                <>
                  {strings.combinedListingArtworkSize}
                  {!!strings.combinedListingArtworkSizeTooltip && (
                    <Tooltip
                      content={
                        <p>
                          <NewLineToBr>
                            {strings.combinedListingArtworkSizeTooltip}
                          </NewLineToBr>
                        </p>
                      }
                      placement="auto"
                      trigger={['click', 'hover']}
                      className="inline-block ml-1"
                      tooltipClassName="py-4 px-6 bg-gray-100"
                      tooltipStyle={{
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.18)',
                      }}
                    >
                      <Icon
                        id="artwork-size-tooltip-icon"
                        name="QuestionMark"
                      />
                    </Tooltip>
                  )}
                </>
              }
              placeholder={strings.combinedListingArtworkSizePlaceholder}
            />
          )}

          {hasSpacerOptions && (
            <InputDropdown
              value={spacerId ? `${spacerId}` : ''}
              setValue={handleSpacerChange}
              label={strings.combinedListingSpacer}
              options={spacerOptions}
            />
          )}
        </>
      )}
    </div>
  )
}

export default CombinedListingProductSelect
